import { useMutation } from "@apollo/client";
import { useParams } from "react-router-dom";
import { CRUD } from "@/shared/components";
import type { EditSchemaDefinition } from "@/shared/components/CRUD/types";
import {
  QueueJobsListQuery,
  QueueJobQuery,
  DeleteQueueJobsMutation,
  RetryQueueJobMutation,
  PromoteQueueJobMutation,
} from "@/admin/entities/queues";
import type { QueueJobQueryQueryType } from "@/types/graphql/admin.generated";
import { QueueJobDetails } from "../domains";
import {
  getColumnsConfig,
  getTableData,
  getTextsConfig,
  mapColumns,
  type QueueJobRow,
} from "../model";

/**
 * Задачи одной очереди в одном статусе. Статус зашит в адрес — на страницу
 * приходят по клику на счётчик в карточке очереди.
 */
export function QueueJobs() {
  const { queueName = "", status = "" } = useParams();

  const mutationOptions = {
    refetchQueries: ["QueueJobsListQuery"],
  };

  const [retry] = useMutation(RetryQueueJobMutation, mutationOptions);
  const [promote] = useMutation(PromoteQueueJobMutation, mutationOptions);

  const columns = getColumnsConfig({
    retryJob: (id: string) => {
      retry({ variables: { queueName, id } });
    },
    promoteJob: (id: string) => {
      promote({ variables: { queueName, id } });
    },
  });

  const editSchema: EditSchemaDefinition = {
    details: {
      label: "Детали задачи",
      type: "show",
      showQuery: QueueJobQuery,
      showQueryVariables: { queueName },
      showComponent: QueueJobDetails,
      showPollInterval: 2000,
      getTitle: (data: unknown) => {
        const { job } = (data || {}) as QueueJobQueryQueryType;
        return job ? job.id : null;
      },
    },
  };

  return (
    <CRUD<QueueJobRow>
      key={`${queueName}/${status}`}
      listQuery={QueueJobsListQuery}
      listQueryVariables={{ queueName, types: [status] }}
      getTableData={getTableData}
      mapColumns={mapColumns}
      columns={columns}
      editSchema={editSchema}
      deleteMutation={DeleteQueueJobsMutation}
      deleteMutationVariables={{ queueName }}
      texts={getTextsConfig(queueName, status)}
    />
  );
}
